import { useContext } from 'react';
import {
  ApiFilterOperation,
  AuthoredEvidence,
  TableCells,
  TableColumn,
  TableFilterCells,
  useStaticListSource,
} from '@eas/common-web';
import { useCountries } from '@modules/dict-country/dict-country-api';
import { useLegalPersons } from '@modules/subject/subject-api';
import { StaticDataContext } from '@components/evidence/static-data-provider';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { FilterTextNumberCell } from '@components/table/filter-text-number-cell';
import {
  AuditCompany,
  DictCountryAutocomplete,
  SubjectAutocomplete,
} from '@models';

export function useColumns(): TableColumn<AuditCompany>[] {
  const { authoredColumns } =
    AuthoredEvidence.useAuthoredColumns<AuditCompany>();

  const { legalForms, licenceStates } = useContext(StaticDataContext);
  const useLegalForms = () => useStaticListSource(legalForms);
  const useLicenceStates = () => useStaticListSource(licenceStates);

  return [
    {
      datakey: 'slovakLicence.number',
      name: 'Číslo licencie',
      width: 150,
      CellComponent: TableCells.TextCell,
      FilterComponent: FilterTextNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 2,
    },
    {
      datakey: 'subject.businessName',
      sortkey: 'subject.businessName',
      filterkey: 'subject.id',
      name: 'Obchodné meno',
      width: 350,
      CellComponent: TableCells.TextCell,
      FilterComponent:
        TableFilterCells.useFilterAutocompleteCellFactory<SubjectAutocomplete>(
          useLegalPersons,
          autocompleteLabelMapper
        ),
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'subject.address.label',
      name: 'Sídlo',
      width: 300,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'subject.identifier',
      name: 'IČO',
      width: 150,
      CellComponent: TableCells.TextCell,
      FilterComponent: FilterTextNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'slovakLicence.state',
      filterkey: 'slovakLicence.state.id',
      sortkey: 'slovakLicence.state.name',
      name: 'Stav licencie',
      width: 150,
      CellComponent: TableCells.TextCell,
      valueMapper: TableCells.useSelectCellFactory(useLicenceStates),
      FilterComponent:
        TableFilterCells.useFilterSelectCellFactory(useLicenceStates),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 2,
    },
    /*
    TODO: Odkomentovat ked bude BE ready
    aby sa zmeny prejavili aj v pripade ze uzivatel ma ulozene zmenene poradie stlpcov
    je potrebne tiez zvysit verziu evidencie:
    subor src\modules\audit-company\audit-companies.tsx => version zvysit o 1
    {
      datakey: 'certificates',
      name: 'Zahraničné certifikáty',
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterGroup: 2,
      filterable: true,
    },
    */
    {
      datakey: 'legalForm',
      filterkey: 'legalForm.id',
      sortkey: 'legalForm.name',
      name: 'Právná forma',
      width: 200,
      CellComponent: TableCells.TextCell,
      valueMapper: TableCells.useSelectCellFactory(useLegalForms),
      FilterComponent:
        TableFilterCells.useFilterSelectCellFactory(useLegalForms),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'homeCountry',
      sortkey: 'homeCountry.name',
      filterkey: 'homeCountry.id',
      name: 'Domovská krajina',
      width: 150,
      CellComponent: TableCells.TextCell,
      valueMapper: TableCells.dictionaryColumnMapper,
      FilterComponent:
        TableFilterCells.useFilterAutocompleteCellFactory<DictCountryAutocomplete>(
          useCountries,
          autocompleteLabelMapper
        ),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'foreignHomeCountry',
      name: 'AS zapísaná podľa § 5 ods. 2',
      width: 150,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    ...authoredColumns.map((c) => ({ ...c, filterGroup: 3 })),
  ];
}
